import { useState } from 'react'
import DatePicker from 'react-datepicker'
import { useNavigate } from 'react-router-dom'
import 'react-datepicker/dist/react-datepicker.css'

import { usePageLoadTracking } from '@/hooks/pageLoadTracking/usePageLoadTracking'

import { PageName } from '@/enums/pageName'
import { getDate } from '@/utils/dateUtils'
import { getPageName } from '@/utils/urlUtils'

import { PageTitle } from '@/components/pageTitle'
import { FooterPolicy } from '@/components/policyLinkBlock'
import { TextBlock } from '@/components/textBlock'
import { TextHTML } from '@/components/typography'
import { Calendar } from '@/shared/icons/Calendar'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'
import { trackPageComplete } from '@/events/events'

import { TEventDatePageProps } from './types'

export function EventDate({
  pageName,
  pageConfig,
  eventPageConfig,
  setItem,
  setLastVisitedItem,
  getItem,
  config,
}: TEventDatePageProps) {
  const { user } = useAuth()
  const sessionContext = useSession()
  const [startDate, setStartDate] = useState<Date | null>(null)

  const navigate = useNavigate()

  const {
    title,
    pageDescription,
    nextUrl,
    skipQuestionUrl,
    calendarIconNode,
    buttonName,
    skipButtonName,
    placeholderText,
    textBlocks,
    policyBlock = false,
  } = pageConfig
  const eventOptions = eventPageConfig.options || []
  const indexFromEvent = getItem(PageName.event) || eventOptions?.length - 1
  const today = new Date()
  //today.setDate(today.getDate() + 7)

  const currentEvent = eventOptions[indexFromEvent as number]
  const currentEventTitle =
    !currentEvent.title || currentEvent.title === 'Other'
      ? 'event'
      : currentEvent.title.toLowerCase()

  let pageTitle = typeof title === 'function' ? title() : title
  pageTitle = pageTitle.replace('{event}', currentEventTitle)

  usePageLoadTracking({
    pageName,
    config,
    user,
    title: pageTitle || '',
  })

  const goToNextPage = async () => {
    const date = startDate ? getDate(new Date(startDate).toString()) : 'No'
    trackPageComplete(
      pageName,
      [date],
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      sessionContext,
      user
    )
    const currentPage = pageName

    setLastVisitedItem(
      skipQuestionUrl && date === 'No' ? getPageName(skipQuestionUrl) : currentPage
    )

    setItem(currentPage, date)

    navigate(skipQuestionUrl && date === 'No' ? skipQuestionUrl : nextUrl)
  }

  const setDate = (date: Date) => {
    if (date) {
      setStartDate(date)
    }
  }

  const nextPage = () => {
    goToNextPage()
  }

  const className = `page page-survey page-${pageName}`
  return (
    <div className={className}>
      <PageTitle title={pageTitle} />

      {pageDescription && <TextHTML className="page-description" title={pageDescription} />}

      <div className="calendar-block">
        <div className="input-container">
          <label htmlFor="datepicker" className="calendar-icon-container">
            {calendarIconNode ? calendarIconNode : <Calendar />}
          </label>
          <DatePicker
            id="datepicker"
            dateFormat="dd MMMM yyyy"
            placeholderText={placeholderText}
            selected={startDate}
            minDate={today}
            onChange={setDate}
          />
        </div>
      </div>
      {textBlocks &&
        textBlocks.map(({ text, ...blockProps }) => {
          text = typeof text === 'function' ? text() : text
          return <TextBlock key={text} text={text} {...blockProps} />
        })}
      <div className="button-container">
        <button className="skip-button" onClick={nextPage}>
          {skipButtonName || 'Skip this question'}
        </button>
        <button className="btn next-button" onClick={nextPage} disabled={!startDate}>
          {buttonName || 'Next'}
        </button>
      </div>
      {policyBlock && <FooterPolicy />}
    </div>
  )
}
